import React from "react";
import { ArrowUp, ArrowDown, Minus } from "lucide-react";
import TradingViewWidget from "./TradingViewWidget";

const money = (n) =>
  `${n < 0 ? "-" : "+"}$${Math.abs(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtDate = (ts) => {
  if (!ts) return "—";
  try {
    return new Date(ts * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return "—";
  }
};

const Stat = ({ label, value, tone }) => (
  <div className="ws-price-cell p-2.5">
    <div className="text-[9px] tracking-[0.14em] uppercase text-[color:var(--ws-muted)] mb-0.5">{label}</div>
    <div className={`ws-heading text-[15px] font-bold ${tone || "text-white"}`}>{value}</div>
  </div>
);

// One combined card per core instrument: live TV mini-chart on top + the trader's REAL edge below.
const InstrumentCard = ({ inst, stats, live, connected }) => {
  const chg = live?.change_pct;
  const chgTone = chg == null ? "text-white" : chg > 0 ? "ws-up" : chg < 0 ? "ws-down" : "text-[color:var(--ws-muted)]";
  const ChgIcon = chg == null || chg === 0 ? Minus : chg > 0 ? ArrowUp : ArrowDown;
  const s = stats;
  const netTone = !s ? "" : s.net > 0 ? "ws-up" : s.net < 0 ? "ws-down" : "text-white";

  return (
    <div className="ws-panel ws-panel-hover p-0 overflow-hidden" data-testid={`instrument-card-${inst.key}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <div className="flex items-center gap-2.5">
          <div className="ws-inst-logo w-9 h-9">{inst.logo ? <img src={inst.logo} alt={inst.label} /> : <span className="ws-icon-chip w-9 h-9 text-[13px] font-bold">{inst.badge}</span>}</div>
          <div>
            <div className="ws-heading font-bold text-[15px] leading-tight">{inst.label}</div>
            <div className="text-[10px] text-[color:var(--ws-muted)] tracking-wide">{inst.sub}</div>
          </div>
        </div>
        <div className="text-right">
          {live ? (
            <>
              <div className="ws-heading font-bold text-[15px] text-white">{live.current?.toLocaleString(undefined, { maximumFractionDigits: 3 })}</div>
              <div className={`text-[11px] font-semibold inline-flex items-center gap-0.5 ${chgTone}`}>
                <ChgIcon className="w-3 h-3" /> {chg == null ? "—" : `${chg > 0 ? "+" : ""}${chg}%`}
              </div>
            </>
          ) : (
            <div className="ws-live-dot" />
          )}
        </div>
      </div>

      {/* Live mini chart */}
      <div className="px-2 ws-mini-tv">
        <TradingViewWidget
          scriptSrc="https://s3.tradingview.com/external-embedding/embed-widget-mini-symbol-overview.js"
          config={{
            symbol: inst.tv,
            width: "100%",
            height: 120,
            locale: "en",
            dateRange: "1M",
            colorTheme: "dark",
            isTransparent: true,
            autosize: false,
            chartOnly: true,
            noTimeScale: true,
            lineWidth: 2,
          }}
        />
      </div>

      {/* Real edge */}
      <div className="px-4 pb-4 pt-1">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] tracking-[0.18em] uppercase text-[color:var(--ws-blue-bright)]">Your Edge</span>
          {s && s.trades > 0 && (
            <span className="text-[10px] text-[color:var(--ws-muted)]">last {fmtDate(s.lastTs)}</span>
          )}
        </div>

        {!connected ? (
          <div className="text-[12px] text-[color:var(--ws-muted)] py-3 text-center">Sign in &amp; sync MT5 to see your edge.</div>
        ) : !s || s.trades === 0 ? (
          <div className="text-[12px] text-[color:var(--ws-muted)] py-3 text-center">No trades on this instrument yet.</div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 mb-2">
              <Stat label="Net P&L" value={money(s.net)} tone={netTone} />
              <Stat label="Win rate" value={`${s.winRate}%`} tone={s.winRate >= 50 ? "ws-up" : "ws-down"} />
              <Stat label="Trades" value={s.trades} />
            </div>
            <div className="grid grid-cols-3 gap-2 mb-2">
              <Stat label="Profit factor" value={s.pf >= 999 ? "∞" : s.pf} tone={s.pf >= 1 ? "ws-up" : "ws-down"} />
              <Stat label="Avg win" value={money(s.avgWin)} tone="ws-up" />
              <Stat label="Avg loss" value={money(s.avgLoss)} tone="ws-down" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Stat label="Best trade" value={money(s.best)} tone="ws-up" />
              <Stat label="Worst trade" value={money(s.worst)} tone="ws-down" />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InstrumentCard;
